import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFilters } from "@/context/FiltersContext";
import { money, fmtDate, moneyLabel } from "@/lib/ui";
import { EntryChips } from "@/components/EntryChips";
import { getChoices, setConfirmation } from "@/lib/choices";
import { downloadICS } from "@/lib/ics";
import WeekendShare from "@/components/WeekendShare";
import { useAuth } from "@/context/AuthContext";
import { M, mStatusPill, mStatusLabel } from "@/lib/mobileTheme";
import { Loader2, ChevronRight, Bell, CalendarPlus, Navigation, Search, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import FilterButton from "@/components/mobile/FilterButton";
import FilterBar from "@/components/mobile/FilterBar";
import AlertsPanel from "@/components/AlertsPanel";
import { toast } from "sonner";

function useRodeos() {
  const [rodeos, setRodeos] = useState(null);
  useEffect(() => { api.get("/rodeos").then((r) => setRodeos(r.data)); }, []);
  return rodeos;
}

function Card({ r, right, onClick, testid, events }) {
  return (
    <button onClick={onClick} data-testid={testid} className={M.card + " w-full text-left p-3.5 flex items-start gap-3"}>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <span className="font-display font-bold text-[16px] text-white leading-tight">{r.name}</span>
          {r.listing
            ? <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded bg-white/10 text-slate-300">DIRECTORY</span>
            : <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded ${mStatusPill(r.status)}`}>{mStatusLabel(r.status)}</span>}
        </div>
        <div className="text-[12px] text-[#F26D77] font-medium mt-0.5">{[r.city, r.province, r.country].filter(Boolean).join(", ")}</div>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          {r.associations.map((a) => <span key={a} className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-white/15 text-slate-400">{a}</span>)}
          {!r.listing && <span className="text-[12px] font-semibold text-slate-200 ml-1">{events ? moneyLabel(r, events) : `${money(r.added_money)} Added`}</span>}
        </div>
        {r.listing ? <div className="text-[11px] text-slate-500 mt-1">{r.dates_text || "Schedule TBA"}</div> : <EntryChips r={r} dark className="mt-1.5" />}
        {right && <div className="mt-2">{right}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-slate-500 shrink-0 mt-1" />
    </button>
  );
}

export function MobileList() {
  const nav = useNavigate();
  const { matches } = useFilters();
  const rodeos = useRodeos();
  const [q, setQ] = useState("");
  const list = (rodeos || []).filter(matches)
    .filter((r) => { const qq = q.trim().toLowerCase(); return !qq || `${r.name} ${r.city} ${r.state} ${(r.associations || []).join(" ")}`.toLowerCase().includes(qq); })
    .sort((a, b) => (a.performances[0]?.date || "").localeCompare(b.performances[0]?.date || ""));
  return (
    <div className={M.page + " pb-24"} data-testid="mobile-list">
      <div className="p-3 sticky top-0 bg-[#0C1017] z-10 border-b border-white/10 space-y-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rodeos, towns…" data-testid="m-list-search-input"
            className="w-full rounded-lg border border-white/10 bg-white/5 text-slate-100 placeholder-slate-500 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F]" />
        </div>
        <FilterBar className="w-full" />
      </div>
      <div className="p-3 space-y-2.5">
        {!rodeos ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
          : list.length === 0 ? <div className="text-center py-24 text-slate-500">No rodeos match.</div>
          : list.map((r) => <Card key={r.id} r={r} onClick={() => nav(`/rodeo/${r.id}`)} testid={`m-list-card-${r.id}`}
              right={<span className="text-xs text-slate-400">{fmtDate(r.performances[0]?.date)}</span>} />)}
      </div>
    </div>
  );
}

// A My Rodeos status pill. "Planned" until the rider saves an entry confirmation number,
// then it flips to green "Entered · #<num>". Tapping opens a small editor.
function EnteredBadge({ rodeoId }) {
  const [conf, setConf] = useState(() => (getChoices()[rodeoId] || {}).confirmation || "");
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const openEditor = (e) => { e.stopPropagation(); e.preventDefault(); setVal(conf); setOpen(true); };
  const save = () => {
    const v = val.trim();
    setConfirmation(rodeoId, v);
    setConf(v);
    setOpen(false);
    toast.success(v ? "Marked as entered" : "Back to planned");
  };
  const remove = () => { setConfirmation(rodeoId, ""); setConf(""); setOpen(false); toast.message("Back to planned"); };
  return (
    <>
      <span role="button" tabIndex={0} onClick={openEditor} data-testid={`m-my-status-${rodeoId}`}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer ${conf ? "bg-[#16A34A]/15 text-[#5CC46A]" : "bg-[#E68A2E]/15 text-[#E68A2E]"}`}>
        {conf ? <><Check className="w-3 h-3" /> Entered · #{conf}</> : "Planned · tap to add confirmation #"}
      </span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0C1017] text-slate-100 border border-white/10 max-w-sm" data-testid="m-confirm-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-display text-white">Entry confirmation</DialogTitle>
            <DialogDescription className="text-slate-400">Paste the confirmation / entry number the association gave you. Adding it marks this rodeo as entered.</DialogDescription>
          </DialogHeader>
          <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="e.g. 12345 or CPRA-88231" data-testid="m-confirm-input"
            className="w-full text-[16px] bg-white/5 border border-white/10 text-slate-100 placeholder-slate-500 rounded-lg px-3 py-2.5 focus:outline-none focus:border-[#C8000F]" />
          <div className="flex items-center gap-2 mt-1">
            <button onClick={save} data-testid="m-confirm-save" className="flex-1 bg-[#C8000F] text-white text-sm font-semibold py-2.5 rounded-lg active:opacity-90">Save</button>
            {conf && <button onClick={remove} data-testid="m-confirm-remove" className="flex items-center gap-1.5 bg-white/10 text-slate-300 text-sm font-semibold px-3 py-2.5 rounded-lg"><X className="w-4 h-4" /> Not entered</button>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function MobileMyRodeos() {  const nav = useNavigate();
  const rodeos = useRodeos();
  const choices = getChoices();
  const { user } = useAuth();
  const { displayEvents } = useFilters();
  const list = (rodeos || []).filter((r) => choices[r.id] && (choices[r.id].pref1 || choices[r.id].pref2));

  const exportCal = () => {
    const evs = [];
    list.forEach((r) => {
      const ch = choices[r.id];
      [["pref1", "1st"], ["pref2", "2nd"]].forEach(([k, lbl]) => {
        const p = r.performances.find((x) => x.id === ch[k]);
        if (p) evs.push({ title: `${r.name} — ${p.label} (${lbl} choice)`, dateISO: p.date, time: p.time, location: `${r.city}, ${r.state}`, details: `${r.associations.join("/")} · ${r.name}` });
      });
    });
    if (!evs.length) return toast.error("No entered performances yet");
    downloadICS(evs);
    toast.success(`Exported ${evs.length} events`);
  };

  return (
    <div className={M.page + " pb-24"} data-testid="mobile-my-rodeos">
      <div className="px-4 py-4 flex items-center justify-between gap-3 border-b border-white/10">
        <h1 className="font-display text-xl font-extrabold text-white">My Rodeos</h1>
        <div className="flex items-center gap-2">
          <WeekendShare list={list} choices={choices} userName={user?.name} variant="dark" />
          <button onClick={exportCal} data-testid="m-export-calendar-btn" className="flex items-center gap-1.5 bg-white/10 text-white text-xs font-semibold px-3 py-2 rounded-lg"><CalendarPlus className="w-4 h-4" /> Calendar</button>
        </div>
      </div>
      <div className="p-3 space-y-2.5">
        <button onClick={() => nav("/planner")} data-testid="m-planner-link-btn" className="w-full flex items-center justify-center gap-2 bg-[#C8000F] text-white text-sm font-semibold py-2.5 rounded-lg"><Navigation className="w-4 h-4" /> Can I Make It? — check my run</button>
        {!rodeos ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
          : list.length === 0 ? <div className="text-center py-24 text-slate-500 px-6">Pick performances on the Calendar to add rodeos here.</div>
          : list.map((r) => <Card key={r.id} r={r} events={displayEvents} onClick={() => nav(`/rodeo/${r.id}`)} testid={`m-my-card-${r.id}`}
              right={<EnteredBadge rodeoId={r.id} />} />)}
      </div>
    </div>
  );
}

export function MobileDeadlines() {
  const nav = useNavigate();
  const rodeos = useRodeos();
  const [alerts, setAlerts] = useState([]);
  useEffect(() => { api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {}); }, []);
  const list = (rodeos || []).filter((r) => !r.listing && r.entry_close).slice().sort((a, b) => a.entry_close.localeCompare(b.entry_close));
  const daysLeft = (iso) => Math.ceil((new Date(iso + "T00:00:00") - new Date()) / 86400000);

  const enableAlerts = async () => {
    if (!("Notification" in window)) return toast.error("Notifications aren't supported on this device");
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      localStorage.setItem("rp_alerts", "1");
      new Notification("Rodeo Pilot", { body: "Deadline alerts on — we'll nudge you before entries close.", icon: "/icon-192.png" });
      toast.success("Deadline alerts enabled");
    } else toast.error("Notification permission denied");
  };

  return (
    <div className={M.page + " pb-24"} data-testid="mobile-deadlines">
      <div className="px-4 py-4 flex items-center justify-between gap-3 border-b border-white/10">
        <h1 className="font-display text-xl font-extrabold text-white">Deadlines</h1>
        <button onClick={enableAlerts} data-testid="m-enable-alerts-btn" className="flex items-center gap-1.5 bg-[#C8000F] text-white text-xs font-semibold px-3 py-2 rounded-lg"><Bell className="w-4 h-4" /> Enable Alerts</button>
      </div>
      <div className="p-3 space-y-2.5">
        <AlertsPanel m={true} />
        {alerts.length > 0 && (
          <div className="rounded-xl bg-[#2A1416] border border-[#C8000F]/30 p-3" data-testid="m-draw-alerts">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#F26D77] mb-2">Draw Changes — who you follow</div>
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <button key={i} onClick={() => nav(`/rodeo/${a.rodeo_id}`)} data-testid={`m-draw-alert-${i}`}
                  className="w-full text-left bg-white/5 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{a.display} <span className="font-normal text-slate-400">· {a.rodeo_name}</span></div>
                    <div className="text-xs text-slate-400 truncate">{a.detail}</div>
                  </div>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#C8000F]/20 text-[#F26D77] shrink-0 uppercase">{a.type}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {!rodeos ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
          : list.map((r) => {
              const dl = daysLeft(r.entry_close);
              const right = dl < 0 ? <span className="text-xs text-slate-500">Entries closed</span>
                : <span className={`text-xs font-semibold ${dl <= 3 ? "text-[#F26D77]" : "text-slate-300"}`}>{dl === 0 ? "Closes today" : `${dl} day${dl === 1 ? "" : "s"} left to enter`}</span>;
              return <Card key={r.id} r={r} onClick={() => nav(`/rodeo/${r.id}`)} testid={`m-dl-card-${r.id}`} right={right} />;
            })}
      </div>
    </div>
  );
}
